import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import EnterpriseIcon, { ChevronDownIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default from 'react';
import GroupSelectionCell from '../RowSelection/GroupSelectionCell.js';
import { getColumnsWithAgg } from '../utils/columnUtils.js';
import { useTable } from '../utils/hooks.js';

/**
 * Group Row - Expandable Parent Row for Row Grouping
 * @param {object} props
 * @param {Array<{ field: String}>} props.columnDefs
 * @param {String} props.id
 * @param {boolean} props.isRowGroupSelected (for group expansion)
 * @param {Function} props.onSelectCallback
 * @param {number} props.parentRowGroupIndex
 * @param {Array<{ field: string}>} props.parentRows
 * @param {string} props.parentValue
 * @returns {any}
 */

function RowGroup(_ref) {
  var classesForColumns = _ref.classesForColumns,
    columnDefs = _ref.columnDefs,
    id = _ref.id,
    isRowGroupSelected = _ref.isRowGroupSelected,
    onSelectCallback = _ref.onSelectCallback,
    parentRowGroupIndex = _ref.parentRowGroupIndex,
    parentRows = _ref.parentRows,
    parentValue = _ref.parentValue,
    groupId = _ref.groupId;
  var _useTable = useTable(),
    _useTable$state = _useTable.state,
    columns = _useTable$state.columns,
    dataTestId = _useTable$state.dataTestId,
    groupDisplayType = _useTable$state.groupDisplayType,
    rowSelection = _useTable$state.rowSelection,
    showGrid = _useTable$state.showGrid;
  var isMultiColumnRowGroup = groupDisplayType === 'multipleColumns';
  var finalClasses = classnames('C-TableAdvanced__bodyRow', {
    expanded: isRowGroupSelected
  });

  //grouping needed for both agg and row selection could eventually move to reducer
  var currentGroupRows = parentRows === null || parentRows === void 0 ? void 0 : parentRows.filter(function (row) {
    return row[columnDefs[parentRowGroupIndex].field] === parentValue;
  });
  var columnsWithAggValues = getColumnsWithAgg(currentGroupRows, columnDefs);
  var classesForRowGroupCell = classnames('C-TableAdvanced__bodyCell', '--rowGroup', {
    '--showGrid': showGrid
  });
  var ExpandableRowGroupCell = function ExpandableRowGroupCell(_ref2) {
    var field = _ref2.field,
      indent = _ref2.indent,
      index = _ref2.index;
    var cellId = "".concat(dataTestId, "-cell-").concat(index);
    return /*#__PURE__*/React__default.createElement("td", {
      className: classesForRowGroupCell,
      "data-testid": cellId,
      key: cellId,
      onClick: function onClick() {
        onSelectCallback(isRowGroupSelected ? null : parentValue);
      }
    }, /*#__PURE__*/React__default.createElement("button", {
      "aria-expanded": isRowGroupSelected,
      "aria-label": "".concat(field, ", click to ").concat(isRowGroupSelected ? 'collapse' : 'expand'),
      className: "C-TableAdvanced__expandButton",
      style: {
        paddingLeft: "calc(".concat(indent, " * 32px)")
      }
    }, /*#__PURE__*/React__default.createElement("div", {
      className: "C-TableAdvanced__expandButton__content"
    }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
      icon: ChevronDownIcon
    }), /*#__PURE__*/React__default.createElement("span", {
      className: "C-TableAdvanced__expandButton__content__text"
    }, field))));
  };
  var SingleColumnRowGroup = function SingleColumnRowGroup() {
    var firstNonRowGroup = columns.columnDefs.find(function (def) {
      return def.rowGroup !== true;
    });
    var ColumnsWithAgg = columnsWithAggValues.splice(firstNonRowGroup.index, columns.columnDefs.length - firstNonRowGroup.index).map(function (col, index) {
      var cellId = "cell-".concat(index + 1);
      var classesForBodyCell = classnames('C-TableAdvanced__bodyCell', {
        '--showGrid': showGrid
      }, classesForColumns[col === null || col === void 0 ? void 0 : col.field]);
      return /*#__PURE__*/React__default.createElement("td", {
        className: classesForBodyCell,
        key: cellId,
        "data-testid": "".concat(dataTestId, "-").concat(cellId)
      }, col === null || col === void 0 ? void 0 : col.value);
    });
    return [/*#__PURE__*/React__default.createElement(ExpandableRowGroupCell, {
      field: parentValue,
      indent: columnDefs[parentRowGroupIndex].index,
      index: 0,
      key: "expandableRowGroupCell",
      rowGroupIndex: columnDefs[parentRowGroupIndex].index
    })].concat(_toConsumableArray(ColumnsWithAgg));
  };
  var MultiColumnRowGroup = function MultiColumnRowGroup() {
    return columnsWithAggValues.map(function (def, index) {
      var cellId = "cell-".concat(index);
      if (parentRowGroupIndex === index) {
        return /*#__PURE__*/React__default.createElement(ExpandableRowGroupCell, {
          field: parentValue,
          key: "expandableRowGroupCell",
          index: index
        });
      }
      var classesForBodyCell = classnames('C-TableAdvanced__bodyCell', {
        '--showGrid': showGrid
      }, classesForColumns[def === null || def === void 0 ? void 0 : def.field]);
      return /*#__PURE__*/React__default.createElement("td", {
        className: classesForBodyCell,
        key: cellId,
        "data-testid": "".concat(dataTestId, "-").concat(cellId)
      }, def === null || def === void 0 ? void 0 : def.value);
    });
  };
  if (parentValue === undefined) {
    return null;
  }
  return /*#__PURE__*/React__default.createElement("tr", {
    className: finalClasses,
    "data-testid": "".concat(dataTestId, "-rowGroup-").concat(id),
    key: "".concat(parentValue, "-rowGroup")
  }, rowSelection && /*#__PURE__*/React__default.createElement(GroupSelectionCell, {
    groupRowData: currentGroupRows,
    dataTestId: "".concat(dataTestId, "-rowGroup-").concat(id),
    groupId: groupId,
    rowSelection: rowSelection
  }), !isMultiColumnRowGroup && /*#__PURE__*/React__default.createElement(SingleColumnRowGroup, null), isMultiColumnRowGroup && /*#__PURE__*/React__default.createElement(MultiColumnRowGroup, null));
}

export { RowGroup as default };
